import {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {ConverterImageLoadError, loadConverterImageFile} from './loadConverterImageFile'
import {addBreadcrumb} from '../../../common/errors/breadcrumb'
import {reportError} from '../../../common/errors/reportError'
import {trackEvent} from '../../../common/analytics/trackEvent'

export interface UseConverterImageUploadResult {
  busy: boolean
  error: string | null
  handleFileSelected: (file: File) => Promise<void>
}

/** Shared validate/load/error/busy logic for picking an image — used by both the
 * source panel's dropzone and the preview panel's own drop target so dropping a
 * file anywhere sensible on the page works the same way. */
export const useConverterImageUpload = (
  hasImage: boolean,
  onImageLoaded: (image: HTMLImageElement, isAnimatedGif: boolean) => void,
): UseConverterImageUploadResult => {
  const {t} = useTranslation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleFileSelected = useCallback(
    async (file: File) => {
      setError(null)
      setBusy(true)
      addBreadcrumb('image-converter', 'source-picked', 'info', {size: file.size, type: file.type})
      trackEvent('image_converter_upload', {format: file.type, size_bytes: file.size, is_replace: hasImage})
      try {
        const img = await loadConverterImageFile(file)
        addBreadcrumb('image-converter', 'source-loaded', 'info', {w: img.width, h: img.height})
        onImageLoaded(img, file.type === 'image/gif')
      } catch (err) {
        if (err instanceof ConverterImageLoadError) {
          setError(t(`imageConverter.upload.${err.key}`))
        } else {
          reportError(err, 'image-converter-load', {size: file.size, type: file.type})
          setError(t('imageConverter.upload.failed'))
        }
      } finally {
        setBusy(false)
      }
    },
    [hasImage, onImageLoaded, t],
  )

  return {busy, error, handleFileSelected}
}
